import React, { useEffect, useState, useRef } from "react";
import { useReactToPrint } from "react-to-print";

import {
    Container,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    CircularProgress,
    Alert,
    TextField,
    Box,
    Divider,
    Grid,
    FormControlLabel,
    Switch,
    Checkbox,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { Print } from "@mui/icons-material";
import { useParams } from "react-router-dom";
import useAuthHeader from "react-auth-kit/hooks/useAuthHeader";
import logo from "../../logo.png";
import {
    numberToWordsWithCurrency,
    numberToArabicWords,
    numberToEnglishWords,
} from "../../Utils/numberToWords";

// Table style for invoice header
const HeaderTable = styled("table")({
    width: "100%",
    tableLayout: "fixed",
    borderCollapse: "collapse",
    "@media print": {
        width: "100%",
        display: "table",
    },
});

const HeaderTableCell = styled("td")({
    width: "33.333%",
    padding: "8px",
    verticalAlign: "top",
    "@media print": {
        width: "33.333%",
        display: "table-cell",
    },
});

// Add print styles as a global style to maintain layout during printing
const GlobalPrintStyles = styled("style")({
    "@media print": {
        "@page": {
            size: "auto",
            margin: "15mm",
        },
        body: {
            margin: "0",
            padding: "0",
        },
        ".print-container": {
            marginTop: "0 !important",
            height: "auto !important",
            overflow: "visible !important",
        },
        ".invoice-header-table": {
            display: "table !important",
            width: "100% !important",
        },
        ".invoice-header-cell": {
            display: "table-cell !important",
            width: "33.333% !important",
        },
        ".no-page-break": {
            pageBreakInside: "avoid !important",
            breakInside: "avoid !important",
        },
        ".page-break-before": {
            pageBreakBefore: "always !important",
            breakBefore: "page !important",
        },
    },
});

// Styled components for elegant invoice design
const InvoiceContainer = styled(Container)(({ theme }) => ({
    maxWidth: "800px",
    padding: "30px",
    border: "1px solid #e0e0e0",
    borderRadius: "8px",
    backgroundColor: "white",
    fontFamily: "'Roboto', sans-serif",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
    position: "relative",
    marginBottom: "40px",
    zIndex: 0,
    "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        backgroundImage: `url(${logo})`,
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
        backgroundSize: "60%",
        opacity: 0.06,
        zIndex: -1,
        display: "none",
        pointerEvents: "none",
        "@media print": {
            display: "block",
            "-webkit-print-color-adjust": "exact",
            "print-color-adjust": "exact",
        },
    },
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
    margin: "20px 0",
    borderRadius: "6px",
    overflow: "hidden",
    "& .MuiTableHead-root": {
        backgroundColor: theme.palette.primary.light,
    },
    "& .MuiTableRow-head": {
        height: "30px",
    },
    "& .MuiTableCell-head": {
        fontWeight: "600",
        color: theme.palette.primary.contrastText,
    },
    "& .MuiTableRow-root:nth-of-type(even)": {
        backgroundColor: "rgba(0, 0, 0, 0.02)",
    },
    "& .MuiTableCell-root": {
        borderBottom: "1px solid rgba(224, 224, 224, 1)",
        padding: "6px 8px",
    },
    "& .MuiTableBody-root .MuiTableRow-root": {
        height: "24px",
    },
    "& .MuiTableBody-root .MuiTableCell-root": {
        padding: "2px 8px",
        fontSize: "11px",
        lineHeight: "1.2",
    },
}));

const tableHeaders = [
    { id: "product", label: "Product", arabicLabel: "المنتج" },
    { id: "quantity", label: "Q", arabicLabel: "الكمية" },
    {
        id: "unitPrice",
        label: "Unit Price",
        arabicLabel: "سعر الوحدة",
        hideWhenNoPrices: true,
    },
    { id: "taxable", label: "Taxable", arabicLabel: "خاضع للضريبة" },
    {
        id: "discountPercentage",
        label: "Discount %",
        arabicLabel: "الخصم %",
        hideWhenNoPrices: true,
    },
    { id: "tax", label: "Tax", arabicLabel: "الضريبة", hideWhenNoPrices: true },
    {
        id: "total",
        label: "Total",
        arabicLabel: "المجموع",
        hideWhenNoPrices: true,
    },
];

const InvoiceViewPage = () => {
    const { id } = useParams(); // Get invoice ID from URL
    const [invoice, setInvoice] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [displayTitle, setDisplayTitle] = useState("");
    const [isArabic, setIsArabic] = useState(false); // Default to Arabic, add toggle
    const [hidePrices, setHidePrices] = useState(false); // Toggle to hide prices for inventory confirmation
    const [subtotalText, setSubtotalText] = useState("");
    const [taxText, setTaxText] = useState("");
    const [totalText, setTotalText] = useState("");
    const [amountInWords, setAmountInWords] = useState("");
    const printRef = useRef(null);
    const reactToPrintFn = useReactToPrint({ contentRef: printRef });
    const authHeader = useAuthHeader();

    useEffect(() => {
        const fetchInvoice = async () => {
            setLoading(true); // Start loading
            setError(null); // Reset error
            try {
                const response = await fetch(
                    `${process.env.REACT_APP_BACKEND_URL}/invoices/${id}`,
                    {
                        method: "GET",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: authHeader,
                        },
                    },
                );
                if (response.ok) {
                    const data = await response.json();
                    console.log("Full Invoice Data:", data.data);
                    console.log("Exchange Rate:", data.data?.dollarToLiraRate);
                    setInvoice(data.data);
                    console.log("Invoice Data:", data.data);
                } else {
                    throw new Error("Failed to fetch invoice data.");
                }
            } catch (error) {
                setError(error.message);
            } finally {
                setLoading(false); // Stop loading
            }
        };
        fetchInvoice();
    }, [authHeader, id]);

    // Auto-generate all text conversions when invoice or language changes
    useEffect(() => {
        if (invoice) {
            const exchangeRate = invoice.dollarToLiraRate
                ? parseFloat(invoice.dollarToLiraRate)
                : null;

            // Generate subtotal text
            if (invoice.priceFirst) {
                const subtotal = numberToWordsWithCurrency(
                    invoice.priceFirst,
                    isArabic,
                    "dollars",
                    exchangeRate,
                );
                setSubtotalText(subtotal);
            }

            // Generate tax text
            if (invoice.priceTax) {
                const tax = numberToWordsWithCurrency(
                    invoice.priceTax,
                    isArabic,
                    "dollars",
                    exchangeRate,
                );
                setTaxText(tax);
            }

            // Generate total text
            if (invoice.priceFinal) {
                const total = numberToWordsWithCurrency(
                    invoice.priceFinal,
                    isArabic,
                    "dollars",
                    exchangeRate,
                );
                setTotalText(total);
                setAmountInWords(total);
            }
        }
    }, [invoice, isArabic]);

    const handlePrint = () => {
        window.print();
    };

    // Loading state
    if (loading) {
        return (
            <div
                style={{
                    display: "flex",
                    justifyContent: "center",
                    marginTop: "20px",
                }}
            >
                <CircularProgress />
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div
                style={{
                    display: "flex",
                    justifyContent: "center",
                    marginTop: "20px",
                }}
            >
                <Alert severity="error">{error}</Alert>
            </div>
        );
    }

    // Invoice not found
    if (!invoice) return <p>No invoice found.</p>;

    // Combine items with the same product ID + priceFinal so that items with different priceFinal show separately
    const aggregatedItems = (() => {
        if (!invoice?.InvoiceItem) return [];
        const map = new Map();
        invoice.InvoiceItem.forEach((item) => {
            // Use item id and priceFinal (fallback to price) to distinguish items with same product but different prices

            console.log(item);
            const key = `${item.item?.id ?? item.itemId}${
                item.priceFinal === "0" ? "-zero" : ""
            }`;
            console.log(key);
            if (map.has(key)) {
                const existing = map.get(key);
                existing.quantity += Number(item.quantity);
                existing.discountAmount += Number(item.discountAmount);
                existing.taxAmount += Number(item.taxAmount); // Sum up taxAmount
                // Aggregate the priceFinal value per line too (use item.priceFinal or compute from price)
                existing.priceFinal += Number(
                    item.priceFinal ??
                        item.quantity * item.price - item.discountAmount,
                );
            } else {
                map.set(key, {
                    ...item,
                    quantity: Number(item.quantity),
                    discountAmount: Number(item.discountAmount),
                    taxAmount: Number(item.taxAmount), // Initialize taxAmount
                    priceFinal: Number(
                        item.priceFinal ??
                            item.quantity * item.price - item.discountAmount,
                    ),
                    // Keep the price for this group (if present)
                    price: Number(item.price ?? 0),
                });
            }
        });
        return Array.from(map.values()).map((item) => ({
            ...item,
            discountPercentage:
                item.price && item.quantity
                    ? (item.discountAmount / (item.quantity * item.price)) * 100
                    : 0,
        }));
    })();

    // Currency Formatter
    const formatCurrency = (value) =>
        new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD",
            minimumFractionDigits: 4,
        }).format(value);

    // Determine if Other Party Info should be displayed
    const showOtherParty = invoice.typeId !== 3;

    // Split items into chunks for pagination (items per page)
    const ITEMS_PER_PAGE = 25; // Adjust this number based on your needs
    const itemChunks = [];
    for (let i = 0; i < aggregatedItems.length; i += ITEMS_PER_PAGE) {
        itemChunks.push(aggregatedItems.slice(i, i + ITEMS_PER_PAGE));
    }

    console.log({ isArabic });
    return (
        <div
            dir={isArabic ? "rtl" : "ltr"}
            style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "flex-start",
                minHeight: "100vh",
                maxWidth: "800px",
                margin: "0 auto",
                paddingTop: "20px",
                paddingBottom: "20px",
                overflowY: "auto",
            }}
            className="print-container"
        >
            {/* Add the GlobalPrintStyles component */}
            <GlobalPrintStyles />

            {!showOtherParty && (
                <TextField
                    value={displayTitle}
                    onChange={(e) => setDisplayTitle(e.target.value)}
                    label="Display Title"
                    sx={{
                        width: "100%",
                        marginBottom: "20px",
                    }}
                />
            )}

            <FormControlLabel
                control={
                    <Switch
                        checked={isArabic}
                        onChange={() => setIsArabic(!isArabic)}
                        color="primary"
                    />
                }
                label="Arabic"
                sx={{ mb: 2 }}
            />

            <FormControlLabel
                control={
                    <Switch
                        checked={hidePrices}
                        onChange={() => setHidePrices(!hidePrices)}
                        color="primary"
                    />
                }
                label={
                    isArabic
                        ? "إخفاء الأسعار (للتأكيد)"
                        : "Hide Prices (For Confirmation)"
                }
                sx={{ mb: 2, ml: 2 }}
            />

            <InvoiceContainer ref={printRef} dir={isArabic ? "rtl" : "ltr"}>
                {itemChunks.map((chunk, pageIndex) => (
                    <Box
                        key={pageIndex}
                        className={pageIndex > 0 ? "page-break-before" : ""}
                        sx={{ mb: pageIndex < itemChunks.length - 1 ? 4 : 0 }}
                    >
                        {/* Show header only on first page or repeat on each page */}
                        {pageIndex === 0 && (
                            <>
                                <Box sx={{ mb: 2 }}>
                                    {/* Replace Grid with Table layout for better printing */}
                                    <HeaderTable className="invoice-header-table">
                                        <tr>
                                            <HeaderTableCell className="invoice-header-cell">
                                                {/* Company Info */}
                                                {!showOtherParty ? (
                                                    <Box>
                                                        <Typography
                                                            variant="h6"
                                                            sx={{
                                                                fontWeight: 500,
                                                            }}
                                                        >
                                                            {displayTitle}
                                                        </Typography>
                                                    </Box>
                                                ) : (
                                                    <Box>
                                                        <Typography
                                                            variant="subtitle1"
                                                            sx={{
                                                                fontWeight: 600,
                                                            }}
                                                        >
                                                            {isArabic
                                                                ? "ضناوي ميد - لاب"
                                                                : "Dannawi Med Lab"}
                                                        </Typography>
                                                        {isArabic ? (
                                                            <Box
                                                                sx={{ mt: 0.5 }}
                                                            >
                                                                <Typography
                                                                    variant="caption"
                                                                    sx={{
                                                                        fontWeight: 600,
                                                                        display:
                                                                            "block",
                                                                    }}
                                                                >
                                                                    ضناوي ميد -
                                                                    لاب
                                                                </Typography>
                                                                <Typography
                                                                    variant="caption"
                                                                    sx={{
                                                                        fontSize:
                                                                            "11px",
                                                                        display:
                                                                            "block",
                                                                    }}
                                                                >
                                                                    تجارة عامة -
                                                                    إستيراد
                                                                    وتصدير مواد
                                                                    طبية ومخبرية
                                                                </Typography>
                                                                <Typography
                                                                    variant="caption"
                                                                    sx={{
                                                                        fontSize:
                                                                            "11px",
                                                                        display:
                                                                            "block",
                                                                    }}
                                                                >
                                                                    العنوان:
                                                                    طرابلس -
                                                                    المعرض -
                                                                    ماناساكي ط 4
                                                                </Typography>
                                                                <Typography
                                                                    variant="caption"
                                                                    sx={{
                                                                        fontSize:
                                                                            "11px",
                                                                        display:
                                                                            "block",
                                                                    }}
                                                                >
                                                                    هاتف:
                                                                    686724\03 -
                                                                    910345\70 |
                                                                    تلفاكس:
                                                                    389016\06
                                                                </Typography>
                                                                <Typography
                                                                    variant="caption"
                                                                    sx={{
                                                                        fontSize:
                                                                            "11px",
                                                                        display:
                                                                            "block",
                                                                    }}
                                                                >
                                                                    الرقم
                                                                    المالي:
                                                                    2786214-601
                                                                </Typography>
                                                            </Box>
                                                        ) : (
                                                            <Box
                                                                sx={{ mt: 0.5 }}
                                                            >
                                                                <Typography
                                                                    variant="caption"
                                                                    sx={{
                                                                        fontSize:
                                                                            "11px",
                                                                        display:
                                                                            "block",
                                                                    }}
                                                                >
                                                                    General
                                                                    Trading -
                                                                    Import and
                                                                    Export of
                                                                    Medical and
                                                                    Laboratory
                                                                    Supplies
                                                                </Typography>
                                                                <Typography
                                                                    variant="caption"
                                                                    sx={{
                                                                        fontSize:
                                                                            "11px",
                                                                        display:
                                                                            "block",
                                                                    }}
                                                                >
                                                                    Address:
                                                                    Tripoli -
                                                                    Al-Maarad -
                                                                    Manasaki
                                                                    Bldg, 4th
                                                                    Floor
                                                                </Typography>
                                                                <Typography
                                                                    variant="caption"
                                                                    sx={{
                                                                        fontSize:
                                                                            "11px",
                                                                        display:
                                                                            "block",
                                                                    }}
                                                                >
                                                                    Phone:
                                                                    03/686724 -
                                                                    70/910345 |
                                                                    Fax:
                                                                    06/389016
                                                                </Typography>
                                                                <Typography
                                                                    variant="caption"
                                                                    sx={{
                                                                        fontSize:
                                                                            "11px",
                                                                        display:
                                                                            "block",
                                                                    }}
                                                                >
                                                                    Tax ID:
                                                                    2786214
                                                                </Typography>
                                                            </Box>
                                                        )}
                                                    </Box>
                                                )}
                                            </HeaderTableCell>

                                            <HeaderTableCell
                                                className="invoice-header-cell"
                                                style={{ textAlign: "center" }}
                                            >
                                                {/* Invoice Info */}
                                                <Box
                                                    sx={{
                                                        textAlign: "center",
                                                        py: 1,
                                                        mb: 1,
                                                    }}
                                                >
                                                    <Typography
                                                        variant="h6"
                                                        sx={{
                                                            color: "text.primary",
                                                            fontWeight: 500,
                                                        }}
                                                    >
                                                        {isArabic
                                                            ? `فاتورة #${
                                                                  invoice.typeId ===
                                                                      1 &&
                                                                  invoice.serialId
                                                                      ? invoice.serialId
                                                                      : id
                                                              }`
                                                            : `Invoice #${
                                                                  invoice.typeId ===
                                                                      1 &&
                                                                  invoice.serialId
                                                                      ? invoice.serialId
                                                                      : id
                                                              }`}
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            color: "text.secondary",
                                                        }}
                                                    >
                                                        {isArabic
                                                            ? "التاريخ:"
                                                            : "Date:"}{" "}
                                                        {new Date(
                                                            invoice.createdAt,
                                                        ).toLocaleDateString()}
                                                    </Typography>
                                                </Box>
                                            </HeaderTableCell>

                                            <HeaderTableCell className="invoice-header-cell">
                                                {/* Customer Info */}
                                                <Box sx={{ mb: 1 }}>
                                                    <Typography
                                                        variant="subtitle1"
                                                        sx={{ fontWeight: 600 }}
                                                    >
                                                        {isArabic
                                                            ? "معلومات العميل"
                                                            : "Customer Information"}
                                                    </Typography>
                                                    <Box
                                                        sx={{
                                                            mt: 0.5,
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                fontSize:
                                                                    "11px",
                                                                display:
                                                                    "block",
                                                                mb: 0.5,
                                                            }}
                                                        >
                                                            <strong>
                                                                {isArabic
                                                                    ? "الاسم:"
                                                                    : "Name:"}
                                                            </strong>{" "}
                                                            {invoice.OtherParty
                                                                ?.name || "N/A"}
                                                        </Typography>
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                fontSize:
                                                                    "11px",
                                                                display:
                                                                    "block",
                                                                mb: 0.5,
                                                            }}
                                                        >
                                                            <strong>
                                                                {isArabic
                                                                    ? "الهاتف:"
                                                                    : "Phone:"}
                                                            </strong>{" "}
                                                            {invoice.OtherParty
                                                                ?.phone ||
                                                                "N/A"}
                                                        </Typography>
                                                        {showOtherParty && (
                                                            <>
                                                                <Typography
                                                                    variant="caption"
                                                                    sx={{
                                                                        fontSize:
                                                                            "11px",
                                                                        display:
                                                                            "block",
                                                                        mb: 0.5,
                                                                    }}
                                                                >
                                                                    <strong>
                                                                        {isArabic
                                                                            ? "العنوان:"
                                                                            : "Address:"}
                                                                    </strong>{" "}
                                                                    {invoice
                                                                        .OtherParty
                                                                        ?.address ||
                                                                        "N/A"}
                                                                </Typography>
                                                                <Typography
                                                                    variant="caption"
                                                                    sx={{
                                                                        fontSize:
                                                                            "11px",
                                                                        display:
                                                                            "block",
                                                                        mb: 0.5,
                                                                    }}
                                                                >
                                                                    <strong>
                                                                        {isArabic
                                                                            ? "الرقم المالي:"
                                                                            : "Tax ID:"}
                                                                    </strong>{" "}
                                                                    {invoice
                                                                        .OtherParty
                                                                        ?.taxNumber ||
                                                                        "N/A"}
                                                                </Typography>
                                                            </>
                                                        )}
                                                    </Box>
                                                </Box>
                                            </HeaderTableCell>
                                        </tr>
                                    </HeaderTable>
                                </Box>

                                <Divider sx={{ mb: 2 }} />
                            </>
                        )}

                        {/* Page indicator for pages after the first */}
                        {pageIndex > 0 && (
                            <Box sx={{ mb: 2, textAlign: "center" }}>
                                <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600 }}
                                >
                                    {isArabic
                                        ? `فاتورة #${
                                              invoice.typeId === 1 &&
                                              invoice.serialId
                                                  ? invoice.serialId
                                                  : id
                                          } - صفحة ${pageIndex + 1}`
                                        : `Invoice #${
                                              invoice.typeId === 1 &&
                                              invoice.serialId
                                                  ? invoice.serialId
                                                  : id
                                          } - Page ${pageIndex + 1}`}
                                </Typography>
                                <Divider sx={{ mt: 1 }} />
                            </Box>
                        )}

                        {/* Invoice Table */}
                        <StyledTableContainer
                            component={Paper}
                            style={{
                                boxShadow: "none",
                            }}
                        >
                            <Table>
                                <TableHead>
                                    <TableRow
                                        style={{
                                            height: "30px",
                                        }}
                                    >
                                        {tableHeaders
                                            .filter(
                                                (header) =>
                                                    !hidePrices ||
                                                    !header.hideWhenNoPrices,
                                            )
                                            .map((header) => (
                                                <TableCell
                                                    key={header.id}
                                                    style={{
                                                        fontWeight: "bold",
                                                        padding: "2px",
                                                        paddingRight: "8px",
                                                        paddingLeft: "8px",
                                                        fontSize: "12px",
                                                        textWrap: "nowrap",
                                                        textAlign: isArabic
                                                            ? "right"
                                                            : "left",
                                                    }}
                                                >
                                                    {isArabic
                                                        ? header.arabicLabel
                                                        : header.label}
                                                </TableCell>
                                            ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {chunk.map((item) => (
                                        <TableRow
                                            key={`${
                                                item.item?.id ?? item.itemId
                                            }-${Number(
                                                item.priceFinal ??
                                                    item.price ??
                                                    0,
                                            ).toFixed(4)}`}
                                            style={{
                                                height: "24px",
                                            }}
                                        >
                                            <TableCell
                                                style={{
                                                    textAlign: isArabic
                                                        ? "right"
                                                        : "left",
                                                    padding: "2px 8px",
                                                    fontSize: "11px",
                                                }}
                                            >
                                                {item.item.name}
                                            </TableCell>
                                            <TableCell
                                                style={{
                                                    textAlign: isArabic
                                                        ? "right"
                                                        : "left",
                                                    padding: "2px 8px",
                                                    fontSize: "11px",
                                                }}
                                            >
                                                {item.quantity}
                                            </TableCell>
                                            {!hidePrices && (
                                                <TableCell
                                                    style={{
                                                        textAlign: isArabic
                                                            ? "right"
                                                            : "left",
                                                        padding: "2px 8px",
                                                        fontSize: "11px",
                                                    }}
                                                >
                                                    {formatCurrency(item.price)}
                                                </TableCell>
                                            )}
                                            <TableCell
                                                style={{
                                                    textAlign: isArabic
                                                        ? "right"
                                                        : "left",
                                                    padding: "2px 8px",
                                                    fontSize: "11px",
                                                }}
                                            >
                                                <Checkbox
                                                    checked={item.isTaxable}
                                                    disabled
                                                    size="small"
                                                    sx={{
                                                        padding: "0px",
                                                        "& .MuiSvgIcon-root": {
                                                            fontSize: "14px",
                                                        },
                                                    }}
                                                />
                                            </TableCell>
                                            {!hidePrices && (
                                                <>
                                                    <TableCell
                                                        style={{
                                                            textAlign: isArabic
                                                                ? "right"
                                                                : "left",
                                                            padding: "2px 8px",
                                                            fontSize: "11px",
                                                        }}
                                                    >
                                                        {Number(
                                                            item.discountPercentage,
                                                        ).toFixed(2)}
                                                        %
                                                    </TableCell>

                                                    <TableCell
                                                        style={{
                                                            textAlign: isArabic
                                                                ? "right"
                                                                : "left",
                                                            padding: "2px 8px",
                                                            fontSize: "11px",
                                                        }}
                                                    >
                                                        {formatCurrency(
                                                            item.taxAmount,
                                                        )}
                                                    </TableCell>
                                                    <TableCell
                                                        style={{
                                                            textAlign: isArabic
                                                                ? "right"
                                                                : "left",
                                                            padding: "2px 8px",
                                                            fontSize: "11px",
                                                        }}
                                                    >
                                                        {formatCurrency(
                                                            item.priceFinal,
                                                        )}
                                                    </TableCell>
                                                </>
                                            )}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </StyledTableContainer>

                        {/* Show totals only on the last page */}
                        {pageIndex === itemChunks.length - 1 && !hidePrices && (
                            <>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 1.5, // Reduced padding
                                        mt: 2, // Reduced top margin
                                        bgcolor: "rgba(0, 0, 0, 0.02)",
                                        borderRadius: "6px",
                                        border: "1px solid rgba(0, 0, 0, 0.08)",
                                    }}
                                    className="no-page-break"
                                >
                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: isArabic
                                                ? "flex-start"
                                                : "flex-end",
                                        }}
                                    >
                                        <Grid
                                            container
                                            spacing={0.5} // Reduced spacing between grid items
                                            sx={{
                                                width: {
                                                    xs: "70%",
                                                    sm: "50%",
                                                    md: "80%",
                                                }, // Made the width smaller
                                            }}
                                        >
                                            <Grid item xs={6}>
                                                <Typography
                                                    variant="caption" // Changed from body2 to caption
                                                    sx={{
                                                        fontWeight: 500,
                                                        fontSize: "10px",
                                                    }} // Reduced font size
                                                >
                                                    {isArabic
                                                        ? "المجموع الفرعي:"
                                                        : "Subtotal:"}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Typography
                                                    variant="caption" // Changed from body2 to caption
                                                    sx={{ fontSize: "10px" }} // Reduced font size
                                                >
                                                    {formatCurrency(
                                                        invoice.priceFirst,
                                                    )}
                                                </Typography>
                                            </Grid>

                                            {subtotalText && (
                                                <Grid item xs={12}>
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            fontSize: "11px",

                                                            color: "black",
                                                            lineHeight: 1.3,
                                                            fontWeight: 500,
                                                        }}
                                                    >
                                                        {subtotalText}
                                                    </Typography>
                                                </Grid>
                                            )}

                                            {showOtherParty && (
                                                <>
                                                    <Grid item xs={6}>
                                                        <Typography
                                                            variant="caption" // Changed from body2 to caption
                                                            sx={{
                                                                fontWeight: 500,
                                                                fontSize:
                                                                    "10px",
                                                            }} // Reduced font size
                                                        >
                                                            {isArabic
                                                                ? `الضريبة (${
                                                                      invoice.taxPercentage ||
                                                                      0
                                                                  }%):`
                                                                : `Tax (${
                                                                      invoice.taxPercentage ||
                                                                      0
                                                                  }%):`}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={6}>
                                                        <Typography
                                                            variant="caption" // Changed from body2 to caption
                                                            sx={{
                                                                fontSize:
                                                                    "10px",
                                                            }} // Reduced font size
                                                        >
                                                            {formatCurrency(
                                                                invoice.priceTax,
                                                            )}
                                                        </Typography>
                                                    </Grid>

                                                    {taxText && (
                                                        <Grid item xs={12}>
                                                            <Typography
                                                                variant="caption"
                                                                sx={{
                                                                    fontSize:
                                                                        "11px",

                                                                    color: "black",
                                                                    lineHeight: 1.3,
                                                                    fontWeight: 500,
                                                                }}
                                                            >
                                                                {taxText}
                                                            </Typography>
                                                        </Grid>
                                                    )}

                                                    {/* Display tax in LBP if exchange rate is provided */}
                                                    {invoice.dollarToLiraRate &&
                                                        invoice.priceTax && (
                                                            <Grid item xs={12}>
                                                                <Typography
                                                                    variant="caption"
                                                                    sx={{
                                                                        fontSize:
                                                                            "11px",

                                                                        color: "black",
                                                                        lineHeight: 1.3,
                                                                        fontWeight: 500,
                                                                    }}
                                                                >
                                                                    {isArabic
                                                                        ? `القيمة المضافة محتسبة بالليرة اللبنانية ${
                                                                              invoice.taxPercentage
                                                                          } 
                                                                         % 
                                                                        ${(
                                                                            parseFloat(
                                                                                invoice.priceTax,
                                                                            ) *
                                                                            parseFloat(
                                                                                invoice.dollarToLiraRate,
                                                                            )
                                                                        ).toFixed(
                                                                            0,
                                                                        )} ل.ل (${numberToArabicWords(
                                                                            Math.round(
                                                                                parseFloat(
                                                                                    invoice.priceTax,
                                                                                ) *
                                                                                    parseFloat(
                                                                                        invoice.dollarToLiraRate,
                                                                                    ),
                                                                            ),
                                                                        )} ليرة لبنانية)`
                                                                        : `VAT calculated in Lebanese Pounds ${(
                                                                              parseFloat(
                                                                                  invoice.priceTax,
                                                                              ) *
                                                                              parseFloat(
                                                                                  invoice.dollarToLiraRate,
                                                                              )
                                                                          ).toFixed(
                                                                              0,
                                                                          )} at ${
                                                                              invoice.taxPercentage
                                                                          }% LBP (${numberToEnglishWords(
                                                                              Math.round(
                                                                                  parseFloat(
                                                                                      invoice.priceTax,
                                                                                  ) *
                                                                                      parseFloat(
                                                                                          invoice.dollarToLiraRate,
                                                                                      ),
                                                                              ),
                                                                          )} Lebanese Pounds)`}
                                                                </Typography>
                                                            </Grid>
                                                        )}
                                                </>
                                            )}

                                            <Grid item xs={12}>
                                                <Divider sx={{ my: 0.3 }} />
                                            </Grid>

                                            <Grid item xs={6}>
                                                <Typography
                                                    variant="body2" // Changed from body1 to body2
                                                    sx={{
                                                        fontWeight: 600,
                                                        fontSize: "11px",
                                                    }} // Reduced font size
                                                >
                                                    {isArabic
                                                        ? "المجموع الكلي:"
                                                        : "Total:"}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Typography
                                                    variant="body2" // Changed from body1 to body2
                                                    sx={{
                                                        fontWeight: 600,
                                                        color: "primary.main",
                                                        fontSize: "11px", // Reduced font size
                                                    }}
                                                >
                                                    {formatCurrency(
                                                        invoice.priceFinal,
                                                    )}
                                                </Typography>
                                            </Grid>

                                            {totalText && (
                                                <Grid item xs={12}>
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            fontSize: "11px",

                                                            color: "black",
                                                            lineHeight: 1.3,
                                                            fontWeight: 500,
                                                        }}
                                                    >
                                                        {totalText}
                                                    </Typography>
                                                </Grid>
                                            )}
                                        </Grid>
                                    </Box>
                                </Paper>

                                <Box
                                    sx={{
                                        mt: 4,
                                        textAlign: "center",
                                        color: "text.secondary",
                                    }}
                                    className="no-page-break"
                                >
                                    <Typography variant="body2">
                                        {isArabic
                                            ? "شكراً لثقتكم بنا"
                                            : "Thank you for your trust"}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        sx={{ mt: 1, display: "block" }}
                                    >
                                        {isArabic
                                            ? "تم إنشاء هذه الفاتورة بتاريخ"
                                            : "This invoice was created on"}{" "}
                                        {new Date().toLocaleDateString()}{" "}
                                        {isArabic ? "الساعة" : "at"}{" "}
                                        {new Date().toLocaleTimeString()}
                                    </Typography>
                                </Box>
                            </>
                        )}

                        {/* Signature section when prices are hidden for inventory confirmation */}
                        {pageIndex === itemChunks.length - 1 && hidePrices && (
                            <>
                                <Box
                                    sx={{
                                        mt: 6,
                                        mb: 4,
                                    }}
                                    className="no-page-break"
                                >
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            textAlign: "center",
                                            mb: 4,
                                            fontWeight: 600,
                                        }}
                                    >
                                        {isArabic
                                            ? "نموذج تأكيد استلام البضاعة"
                                            : "Inventory Receipt Confirmation"}
                                    </Typography>
                                    <Grid container spacing={4}>
                                        <Grid item xs={6}>
                                            <Box>
                                                <Typography
                                                    variant="body2"
                                                    sx={{ mb: 1 }}
                                                >
                                                    {isArabic
                                                        ? "الاسم:"
                                                        : "Name:"}
                                                </Typography>
                                                <Divider
                                                    sx={{
                                                        borderBottomWidth: 2,
                                                        borderColor: "black",
                                                    }}
                                                />
                                            </Box>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Box>
                                                <Typography
                                                    variant="body2"
                                                    sx={{ mb: 1 }}
                                                >
                                                    {isArabic
                                                        ? "التاريخ:"
                                                        : "Date:"}
                                                </Typography>
                                                <Divider
                                                    sx={{
                                                        borderBottomWidth: 2,
                                                        borderColor: "black",
                                                    }}
                                                />
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Box>
                                                <Typography
                                                    variant="body2"
                                                    sx={{ mb: 1 }}
                                                >
                                                    {isArabic
                                                        ? "التوقيع:"
                                                        : "Signature:"}
                                                </Typography>
                                                <Divider
                                                    sx={{
                                                        borderBottomWidth: 2,
                                                        borderColor: "black",
                                                    }}
                                                />
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Box>

                                <Box
                                    sx={{
                                        mt: 4,
                                        textAlign: "center",
                                        color: "text.secondary",
                                    }}
                                    className="no-page-break"
                                >
                                    <Typography variant="body2">
                                        {isArabic
                                            ? "يرجى التوقيع أعلاه لتأكيد استلام جميع العناصر المدرجة"
                                            : "Please sign above to confirm receipt of all listed items"}
                                    </Typography>
                                </Box>
                            </>
                        )}
                    </Box>
                ))}
            </InvoiceContainer>
            <Button
                variant="contained"
                color="primary"
                onClick={() => reactToPrintFn()}
                sx={{
                    mt: 3,
                    mb: 5,
                    px: 4,
                    py: 1.2,
                    borderRadius: "24px",
                    boxShadow: "0 4px 10px rgba(0, 0, 0, 0.15)",
                }}
                startIcon={<Print />}
            >
                {isArabic ? "طباعة الفاتورة" : "Print Invoice"}
            </Button>
        </div>
    );
};

export default InvoiceViewPage;
